<!-- FileInput.vue -->
<script setup lang="ts">
import galleryIcon from '@/assets/icons/green/galery.svg'
import deleteIcon from '@/assets/icons/grey/close.svg'
import photoIcon from '@/assets/icons/white/camera.svg'

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import CropDialog from '@/components/CropDialog/CropDialog.vue'
import Button from '@/components/Button.vue'

/** Props */
const props = withDefaults(
  defineProps<{
    /** Aceites do input nativo; se preenchido, sobrepõe o comportamento dinâmico */
    accept?: string
    /** URL relativa/absoluta ou dataURL para pré-visualização inicial */
    filePreview?: string
    /** Máscara visual do recorte (não define tipo final do arquivo) */
    type?: 'circle' | 'square'
    /**
     * Controla o formato final do arquivo gerado após o crop.
     *  - 'jpeg' => força image/jpeg (re-encoda de verdade via canvas)
     *  - 'png'  => força image/png  (re-encoda via canvas)
     *  - 'auto' => heurística (circle->png, square->jpeg)
     */
    outputFormat?: 'auto' | 'jpeg' | 'png'
  }>(),
  {
    accept: '', // ✅ adiciona default
    filePreview: '', // ✅ adiciona default
    type: 'square',
    outputFormat: 'auto',
  }
)

/** Emits */
const emit = defineEmits<{
  (e: 'change', file: File | undefined): void
  (e: 'save', file: File | undefined): void
  (e: 'delete', file: File | undefined): void
}>()

const { t } = useI18n()
const toast = useToast()

/** Estado */
const reader = new FileReader()
const dialogs = reactive({ cropPicture: false })

const localFile = ref<File | undefined>()
const localFilePreview = ref<string | undefined>()
const pendingFile = ref<File | null>(null)

/** Tipos permitidos (apenas para o input/UX) */
const defaultAllowedTypes = computed<string[]>(() => {
  const out = props.outputFormat ?? 'auto'
  if (out === 'jpeg') return ['image/jpeg']
  if (out === 'png') return ['image/png']
  // 'auto': segue a heurística antiga
  return props.type === 'circle' ? ['image/png'] : ['image/jpeg']
})

/** Accept do <input type="file"> */
const acceptAttr = computed(() => {
  if (props.accept?.trim()) return props.accept
  return defaultAllowedTypes.value.join(',')
})

/** Texto com formatos aceitos */
const allowedFormatsLabel = computed(() =>
  props.accept?.trim()
    ? props.accept
        .split(',')
        .map((s) => s.trim().replace('image/', ''))
        .join(', ')
    : (props.outputFormat ?? 'auto') === 'jpeg'
      ? 'jpeg'
      : (props.outputFormat ?? 'auto') === 'png'
        ? 'png'
        : props.type === 'circle'
          ? 'png'
          : 'jpeg'
)

/** Preview: prioriza local, senão usa a prop filePreview */
const preview = computed(() =>
  localFilePreview.value
    ? localFilePreview.value
    : props.filePreview?.includes('http')
      ? props.filePreview
      : props.filePreview
        ? `${import.meta.env.VITE_API_URL}/resources/${props.filePreview}`
        : undefined
)

const hasFile = computed(() => !!localFile.value || !!props.filePreview)

/** Ações básicas */
function removefile() {
  localFile.value = undefined
  localFilePreview.value = undefined
  pendingFile.value = null
  emit('delete', localFile.value)
}

/** Validação leve por tipo de arquivo (apenas se não houver accept explícito) */
function validateType(file: File) {
  if (props.accept?.trim()) return true
  return defaultAllowedTypes.value.includes(file.type)
}

/** Drag & Drop */
function handleDrop(event: DragEvent) {
  event.preventDefault()

  const files = event.dataTransfer?.files
  if (!files || files.length === 0) return

  const newFile = files[0]
  pendingFile.value = newFile

  if (!validateType(newFile)) {
    toast.error(t('general.uploadError'))
    return
  }

  reader.onload = () => {
    localFilePreview.value = reader.result as string
    dialogs.cropPicture = true
  }
  reader.readAsDataURL(newFile)
}

/** Input de arquivo tradicional */
function handleFileInput(event: Event) {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length > 0) {
    const newFile = input.files[0]
    pendingFile.value = newFile

    if (!validateType(newFile)) {
      toast.error(t('general.uploadError'))
      input.value = ''
      return
    }

    reader.onload = () => {
      localFilePreview.value = reader.result as string
      dialogs.cropPicture = true
    }
    reader.readAsDataURL(newFile)
  }
  // permite re-selecionar o mesmo arquivo
  input.value = ''
}

/** Abre o seletor de arquivo */
function openFileInput(): void {
  const inputFile = document.getElementById(
    'file-input'
  ) as HTMLInputElement | null
  if (!inputFile) return
  inputFile.click()
}

/** Decide MIME final a partir de outputFormat/type */
function desiredMime(): 'image/jpeg' | 'image/png' {
  const out = props.outputFormat ?? 'auto'
  if (out === 'jpeg') return 'image/jpeg'
  if (out === 'png') return 'image/png'
  // 'auto'
  return props.type === 'circle' ? 'image/png' : 'image/jpeg'
}

/** Utils de transcodificação real via canvas (converte bytes de fato) */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })
}

function toBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Canvas toBlob failed'))
      },
      mime,
      quality
    )
  })
}

/**
 * Re-encoda um dataURL para o mime desejado usando canvas.
 * - Aplica fundo branco quando exporta JPEG (para evitar “fundo preto” ao achatar transparência).
 */
async function transcodeDataUrl(
  dataUrl: string,
  mime: 'image/jpeg' | 'image/png',
  quality = 0.92
): Promise<Blob> {
  const img = await loadImage(dataUrl)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth || img.width
  canvas.height = img.naturalHeight || img.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2D context not available')

  if (mime === 'image/jpeg') {
    // JPEG não tem alpha: pinta fundo branco para “achatar” a transparência
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  return await toBlob(canvas, mime, mime === 'image/jpeg' ? quality : undefined)
}

/** Recebe o dataURL do CropDialog e cria um File coerente com o formato desejado (re-encodado) */
async function handleCrop(dataUrl: string | null) {
  dialogs.cropPicture = false
  if (!dataUrl) return

  const mime = desiredMime()
  const fallbackName =
    mime === 'image/png' ? 'cropped-image.png' : 'cropped-image.jpg'

  const fileName = pendingFile.value?.name
    ? pendingFile.value.name.replace(
        /\.(png|jpg|jpeg|webp|gif)$/i,
        mime === 'image/png' ? '.png' : '.jpg'
      )
    : fallbackName

  // Re-encode de verdade (NÃO apenas trocar o .type)
  const encodedBlob = await transcodeDataUrl(dataUrl, mime, 0.92)
  const newFile = new File([encodedBlob], fileName, { type: mime })

  localFile.value = newFile
  localFilePreview.value = URL.createObjectURL(encodedBlob)
  pendingFile.value = null

  emit('change', newFile)
  emit('save', newFile)
}
</script>

<template>
  <!-- input invisível -->
  <input
    id="file-input"
    type="file"
    name="inputFileTerms"
    style="display: none"
    :accept="acceptAttr"
    @change="handleFileInput"
  />

  <!-- Dropzone -->
  <div
    v-if="!hasFile"
    class="d-flex flex-column align-center justify-space-evenly rounded bg-white text-center"
    style="
      width: 100%;
      height: 96px;
      border: 2px dashed #cbd5e1;
      cursor: pointer;
    "
    @dragover.prevent
    @drop="handleDrop"
    @click="openFileInput"
  >
    <v-img :src="galleryIcon" width="25" class="mb-1" />
    <div class="text-caption font-weight-bold">
      {{ $t('general.choseFileImage') }}
    </div>
    <div class="text-caption font-weight-light">
      {{ $t('general.clickOrDrag') }}
    </div>
    <div class="text-caption">
      {{ $t('general.allowedImageFormats', { formats: allowedFormatsLabel }) }}
    </div>
  </div>

  <!-- Preview + ações -->
  <div
    v-else
    class="d-flex flex-column align-center pa-4 justify-center rounded bg-white"
    style="width: 100%"
  >
    <div
      class="d-flex align-center justify-center"
      style="width: 100%; gap: 1rem"
    >
      <div
        class="d-flex position-relative overflow-hidden"
        style="width: 96px; max-height: 96px; flex-shrink: 0"
      >
        <img
          id="file-input-preview"
          :src="preview"
          :style="{
            width: '96px',
            padding: '8px',
            borderRadius: props.type === 'circle' ? '9999px' : '12px',
          }"
        />
        <v-img
          :src="deleteIcon"
          alt="delete"
          width="20"
          class="position-absolute"
          style="
            top: 20%;
            right: -12px;
            transform: translate(-50%, -50%);
            border-radius: 9999px;
            background-color: white;
            cursor: pointer;
          "
          @click="removefile"
        />
      </div>

      <div class="pl-4" style="flex-grow: 1">
        <Button @click="openFileInput">
          <v-img :src="photoIcon" width="20" class="me-2" />
          {{ $t('corporate.edit.changePicture') }}
        </Button>
      </div>
    </div>

    <div class="text-body-2 text-grey mt-4 text-left" style="width: 100%">
      {{ $t('corporate.edit.profilePictureHint') }}
    </div>
  </div>

  <!-- Dialog de crop -->
  <CropDialog
    :open="dialogs.cropPicture"
    :image="localFilePreview"
    :type="props.type"
    @update:dialog="(v: boolean) => (dialogs.cropPicture = v)"
    @close="
      (() => {
        dialogs.cropPicture = false
        removefile()
      })()
    "
    @save="handleCrop"
  />
</template>
